import { useId } from 'react';
import { cx } from '../lib/cx.js';
import './shell.css';

export interface BrandMarkProps {
  /**
   * Product name for the accessible label (and the text wordmark fallback).
   * Pass `PRODUCT.shortName` from @velora/contracts; the name lives in that
   * one constant.
   */
  name: string;
  /** Mark height in px. The simplified mark is tuned for 16 to 32 px and scales up cleanly. */
  size?: number;
  /** Show the wordmark beside the mark. */
  wordmark?: boolean;
  /**
   * Wordmark style. `logo` draws the geometric VELORA letterforms from the
   * concept logo (orange V stroke and A notch); `text` sets `name` in the UI
   * face. Defaults to `logo` when `name` is Velora and `text` otherwise, so
   * a rename (PRODUCT) never shows the old letterforms.
   */
  wordmarkStyle?: 'logo' | 'text';
  /** Single-colour mark (current text colour), for print and tight chrome. */
  mono?: boolean;
  className?: string;
}

/** The tooth ribbon: a crown with two lobes and two roots. */
const TOOTH =
  'M9.4 3.6C5.6 3.6 3 6.5 3 10.7c0 3.8 1.4 6.6 2.4 10.2 1 3.6 1.5 8.4 4.5 8.4 2.6 0 3-3.7 3.7-6.9.4-1.8 1.3-2.8 2.4-2.8s2 1 2.4 2.8c.7 3.2 1.1 6.9 3.7 6.9 3 0 3.5-4.8 4.5-8.4 1-3.6 2.4-6.4 2.4-10.2 0-4.2-2.6-7.1-6.4-7.1-2.9 0-4.7 1.7-6.6 1.7S12.3 3.6 9.4 3.6Z';
/** The orange V ribbon, woven over the crown. */
const V_RIBBON = 'M7.4 7.6 16 21l8.6-13.4';

/**
 * The simplified small mark (BUILD-PLAN 3.2 brand assets): the concept
 * logo's tooth ribbon in brand navy with the orange-gradient V woven over
 * it (the V knocks a gap out of the tooth, so it reads as a ribbon on any
 * background). Original vector drawing, not a trace of the raster concept.
 */
export function BrandGlyph({
  size = 24,
  mono = false,
  className,
}: {
  size?: number;
  mono?: boolean;
  className?: string;
}) {
  const id = useId().replace(/:/g, '');
  const ink = `${id}-ink`;
  const orange = `${id}-or`;
  const knock = `${id}-knock`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden
      className={cx('vel-brand__mark', className)}
      data-mono={mono ? '' : undefined}
    >
      <defs>
        <linearGradient id={ink} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0%" className="vel-brand__ink-a" />
          <stop offset="100%" className="vel-brand__ink-b" />
        </linearGradient>
        <linearGradient id={orange} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" className="vel-brand__stop-a" />
          <stop offset="50%" className="vel-brand__stop-b" />
          <stop offset="100%" className="vel-brand__stop-c" />
        </linearGradient>
        <mask id={knock} maskUnits="userSpaceOnUse" x="0" y="0" width="32" height="32">
          <rect width="32" height="32" className="vel-brand__mask-keep" />
          <path
            d={V_RIBBON}
            className="vel-brand__mask-cut"
            fill="none"
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </mask>
      </defs>
      <path
        d={TOOTH}
        mask={`url(#${knock})`}
        className="vel-brand__tooth"
        style={mono ? undefined : { stroke: `url(#${ink})` }}
        fill="none"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <path
        d={V_RIBBON}
        className="vel-brand__v"
        style={mono ? undefined : { stroke: `url(#${orange})` }}
        fill="none"
        strokeWidth="3.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The VELORA wordmark as geometric letterforms (concept logo): heavy caps,
 * the V's right stroke in the orange gradient and an orange notch in the A.
 * `height` is the cap height in px.
 */
export function BrandWordmark({
  height = 16,
  mono = false,
  className,
}: {
  height?: number;
  mono?: boolean;
  className?: string;
}) {
  const id = useId().replace(/:/g, '');
  const clip = `${id}-clip`;
  const orange = `${id}-wor`;
  return (
    <svg
      height={height}
      width={(height * 104) / 20}
      viewBox="0 0 104 20"
      aria-hidden
      className={cx('vel-brand__wordmark', className)}
      data-mono={mono ? '' : undefined}
    >
      <defs>
        <clipPath id={clip}>
          <rect x="-2" y="0" width="110" height="20" />
        </clipPath>
        <linearGradient id={orange} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" className="vel-brand__stop-a" />
          <stop offset="100%" className="vel-brand__stop-c" />
        </linearGradient>
      </defs>
      <g clipPath={`url(#${clip})`} className="vel-brand__letters" strokeWidth="3.4" fill="none">
        {/* V: the right stroke carries the orange */}
        <path d="M0.6 -2 7.6 21.5" />
        <path
          d="M7.6 21.5 14.6 -2"
          className="vel-brand__accent"
          style={mono ? undefined : { stroke: `url(#${orange})` }}
        />
        {/* E */}
        <path
          className="vel-brand__solid"
          d="M18.6 0h3.4v20h-3.4zM18.6 0h11v3.3h-11zM18.6 8.4h10v3.2h-10zM18.6 16.7h11V20h-11z"
        />
        {/* L */}
        <path className="vel-brand__solid" d="M33.4 0h3.4v20h-3.4zM33.4 16.7h10.5V20H33.4z" />
        {/* O */}
        <circle cx="56.6" cy="10" r="8.3" />
        {/* R */}
        <path className="vel-brand__solid" d="M69.6 0H73v20h-3.4z" />
        <path d="M71.2 1.7h5.6a4.75 4.75 0 0 1 0 9.5h-5.6" />
        <path d="M75.8 11 82.7 21" />
        {/* A: no bar, an orange notch in the counter */}
        <path d="M85.6 21.5 92.6 -2 99.6 21.5" strokeLinejoin="miter" />
        <path
          d="M92.6 11.6 95.6 20h-6z"
          className="vel-brand__notch"
          style={mono ? undefined : { fill: `url(#${orange})` }}
        />
      </g>
    </svg>
  );
}

/** Mark plus optional wordmark: the product lockup for chrome and sign-in. */
export function BrandMark({
  name,
  size = 24,
  wordmark = false,
  wordmarkStyle,
  mono = false,
  className,
}: BrandMarkProps) {
  // The drawn letterforms spell VELORA; a renamed product falls back to text.
  const style = wordmarkStyle ?? (/^velora$/i.test(name.trim()) ? 'logo' : 'text');
  return (
    <span className={cx('vel-brand', className)} role="img" aria-label={name}>
      <BrandGlyph size={size} mono={mono} />
      {wordmark ? (
        style === 'logo' ? (
          <BrandWordmark height={Math.max(10, Math.round(size * 0.52))} mono={mono} />
        ) : (
          <span className="vel-brand__word" aria-hidden>
            {name}
          </span>
        )
      ) : null}
    </span>
  );
}
